import React, { useEffect, useState } from 'react';

import apiClient from '../services/api';
import Modal from './Modal';
import PlatformBadge from './PlatformBadge';
import './PublishConfirmModal.css';

interface PreflightMedia {
  id: number;
  media_type: 'image' | 'video' | 'audio';
  order: number;
  url: string | null;
}

interface PreflightCheck {
  account_id: number;
  platform: string;
  account_name: string;
  ok: boolean;
  message: string;
  details: Record<string, unknown>;
  /** Instagram rows only: what this account will actually do. */
  instagram_plan?: {
    feed: boolean;
    story: boolean;
    collaborator: boolean;
  };
}

function igPlanLabel(plan: NonNullable<PreflightCheck['instagram_plan']>): string {
  const parts: string[] = [];
  if (plan.collaborator) parts.push('collaborator invite');
  if (plan.feed) parts.push('feed');
  if (plan.story) parts.push('story');
  return parts.join(' + ');
}

interface PreflightResult {
  post_id: number;
  description: string;
  media: PreflightMedia[];
  checks: PreflightCheck[];
  all_ok: boolean;
}

interface PublishConfirmModalProps {
  postId: number | null;
  onClose: () => void;
  onConfirm: () => void;
}

export default function PublishConfirmModal({ postId, onClose, onConfirm }: PublishConfirmModalProps) {
  const [data, setData] = useState<PreflightResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!postId) {
      setData(null);
      setLoading(true);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    apiClient.post<PreflightResult>(`/posts/posts/${postId}/preflight/`, {})
      .then(setData)
      .catch((err: Error) => setError(err.message || 'Preflight failed'))
      .finally(() => setLoading(false));
  }, [postId]);

  const okCount = data?.checks.filter((c) => c.ok).length ?? 0;
  const failCount = (data?.checks.length ?? 0) - okCount;
  const allOk = data?.all_ok ?? false;

  return (
    <Modal
      title="Review before publishing"
      open={postId !== null}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="primary"
            onClick={onConfirm}
            disabled={loading || !data || okCount === 0}
            title={failCount > 0 && allOk === false
              ? `${failCount} platform check${failCount === 1 ? '' : 's'} failed — publish anyway will skip the failing ones.`
              : undefined}
          >
            {allOk
              ? `Looks good — publish to ${okCount}`
              : failCount > 0 && okCount > 0
                ? `Publish to the ${okCount} ready`
                : 'Publish'}
          </button>
        </>
      }
    >
      {loading && <p className="muted">Running preflight checks…</p>}
      {error && <div className="banner banner--error">{error}</div>}

      {data && (
        <>
          <section className="preview-card">
            <div className="section-label">Post preview</div>
            <div className="preview-body">{data.description || <em className="muted">(no text)</em>}</div>
            {data.media.length > 0 && (
              <ul className="preview-media">
                {data.media.map((m) => (
                  <li key={m.id} className={`preview-media-tile kind-${m.media_type}`}>
                    {m.media_type === 'image' && m.url && <img alt="" src={m.url} />}
                    {m.media_type === 'video' && m.url && <video src={m.url} muted preload="metadata" playsInline />}
                    {m.media_type === 'audio' && <div className="preview-audio">♪ audio</div>}
                    <span className="preview-media-kind">{m.media_type}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <div className="section-label">Platforms ({okCount} of {data.checks.length} ready)</div>
            <ul className="platform-check-list">
              {data.checks.map((c) => (
                <li key={c.account_id} className={`platform-check ${c.ok ? 'ok' : 'fail'}`}>
                  <div className="platform-check-row">
                    <PlatformBadge platform={c.platform} />
                    <span className="platform-check-name">{c.account_name}</span>
                    {c.instagram_plan && (
                      <span className="badge">{igPlanLabel(c.instagram_plan)}</span>
                    )}
                    <span className={`platform-check-status ${c.ok ? 'ok' : 'fail'}`}>
                      {c.ok ? '✓ ready' : '✗ blocked'}
                    </span>
                  </div>
                  {c.message && <div className="platform-check-message">{c.message}</div>}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </Modal>
  );
}
